import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { PwaService } from '../pwa/pwa.service';
import { StorageService } from '../storage/storage.service';
import { NOTIFICATION_STATE_NAME } from 'src/app/app.config';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  webPushSupported: boolean = true;

  constructor(
    private translocoService: TranslocoService,
    private pwaService: PwaService,
    private deviceDetectorService: DeviceDetectorService,
    private apiService: ApiService,
    private readonly messaging: AngularFireMessaging,
    private storageService: StorageService
  ) {
    this.webPushSupported =
      deviceDetectorService.os != 'iOS' || pwaService.isInstalled();
  }

  /**
   * Listener for push notifications when app is opened
   * @returns an Observable with the messages received from the server
   */
  listenerForMessages(): Observable<any> {
    return this.messaging.messages;
  }

  /**
   * Check if the device supports push notifications
   * @returns true if the current device supports push notifications
   */
  isSupported(): boolean {
    return this.webPushSupported;
  }

  /**
   * Detect when the token changes. This function is called from app.components.ts
   */
  onTokenChanges(): void {
    this.messaging.getToken.subscribe({
      next: (token) => {
        let notifications = this.storageService.getValue(
          NOTIFICATION_STATE_NAME
        );
        // notification must be present and the object must have token property (null or other value)
        if (notifications && notifications['token']) {
          if (notifications.token != token) {
            this.pwaService.getUserUID(true).subscribe({
              next: (uid) => {
                this.apiService
                  .updateNotificationToken({
                    provider: 'fcm',
                    appkey: this.storageService.getUID(),
                    status: token != null,
                    eid: token,
                    uid: uid,
                  })
                  .subscribe({
                    next: (status) => {
                      let old_status = this.storageService.getValue(
                        NOTIFICATION_STATE_NAME
                      );
                      old_status.token = token;
                      this.storageService.saveValue(
                        NOTIFICATION_STATE_NAME,
                        old_status
                      );
                      console.log(
                        'Notification state in local storage updated'
                      );
                    },
                    error: (err) => console.error(err),
                  });
              },
              error: (err) => console.error(err),
            });
          }
        }
      },
      error: (err) => console.error(err),
    });
  }

  /**
   * Function used to subscribe to push notifications.
   * @param webPush enables web push notifications with FCM
   * @param email email address which will be used to send email notifications (if available)
   */
  subscribe(
    webPush: boolean,
    email: string | boolean | null = null
  ): Observable<string | null> {
    return new Observable<any>((observer) => {
      if (webPush) {
        this.messaging.requestPermission.subscribe({
          next: (permission) => {
            if (permission == 'granted') {
              this.messaging.getToken.subscribe({
                next: (token) => {
                  this.sendToServer(token, email).subscribe({
                    next: () => {
                      const old_status = this.storageService.getValue(
                        NOTIFICATION_STATE_NAME
                      );
                      if (
                        token &&
                        (!old_status ||
                          (old_status && old_status['token'] == null))
                      ) {
                        this.apiService
                          .sendWelcomePushNotification(token)
                          .subscribe({
                            next: () =>
                              console.log('Welcome push notification sent'),
                            error: (err) => console.error(err),
                          });
                      }
                      observer.next(token);
                    },
                    error: (err) => observer.error(err),
                  });
                },
                error: (err) => observer.error(err),
              });
            } else
              this.translocoService
                .selectTranslate('notification_permission_not_garanted')
                .subscribe((message) => {
                  observer.error(message);
                });
          },
          error: (err) => observer.error(err),
        });
      } else {
        this.sendToServer(null, email).subscribe({
          next: (token) => observer.next(null),
          error: (err) => observer.error(err),
        });
      }
    });
  }

  private sendToServer(
    webPush: string | null,
    email: string | boolean | null = null
  ): Observable<any> {
    return new Observable<any>((observer) => {
      this.pwaService.getUserUID(true).subscribe({
        next: (uid) => {
          this.apiService
            .updateNotificationState({
              provider: 'fcm',
              appkey: this.storageService.getUID(), //TODO inserito UID
              uid: uid,
              eid: webPush,
              telegram: false,
              email: email,
              status: webPush != null,
            })
            .subscribe({
              next: (status) => observer.next(status),
              error: (err) => observer.error(err),
            });
        },
        error: (err) => observer.error(err),
      });
    });
  }
}
